if (!customElements.get("push-cart")) {
  customElements.define(
    "push-cart",
    class PushCart extends window.BAO.CustomElement {
      _loading = false;
      _deferredFetchId = 0;
      _cartUpdatePending = 0;

      get loading() {
        return this._loading;
      }

      set loading(isLoading) {
        this._loading = isLoading;
        this.classList.toggle("psh-Cart-loading", isLoading);
        this.toggleLoadingOverlay(isLoading);
      }

      sections = window.BAO.utils.sectionRenderer.pushCartSections;

      constructor() {
        super();

        if (this.drawer == null) {
          throw new Error(
            `<cart-drawer> needs to be used within a <site-drawer> element`
          );
        }
      }

      setupListeners() {
        super.setupListeners();

        this.onBeforeCartAdd = this.onBeforeCartAdd.bind(this);
        this.onAfterCartAdd = this.onAfterCartAdd.bind(this);
        this.beforeUpdateItemQuantity = this.beforeUpdateItemQuantity.bind(this);
        this.afterUpdateItemQuantity = this.afterUpdateItemQuantity.bind(this);
        this.syncCartItemsStickyFooter =
          this.syncCartItemsStickyFooter.bind(this);

        window.BAO.eventBus.addEventListener(
          window.BAO.EVENTS.PUSH_CART.BEFORE_UPDATE_ITEM_QUANTITY,
          this.beforeUpdateItemQuantity
        );

        window.BAO.eventBus.addEventListener(
          window.BAO.EVENTS.PUSH_CART.AFTER_UPDATE_ITEM_QUANTITY,
          this.afterUpdateItemQuantity
        );

        window.BAO.eventBus.addEventListener(
          window.BAO.EVENTS.PUSH_CART.BEFORE_ADD,
          this.onBeforeCartAdd
        );

        window.BAO.eventBus.addEventListener(
          window.BAO.EVENTS.PUSH_CART.AFTER_ADD,
          this.onAfterCartAdd
        );

        this.syncCartItemsStickyFooter();
      }

      disconnectedCallback() {
        super.disconnectedCallback();

        window.BAO.eventBus.removeEventListener(
          window.BAO.EVENTS.PUSH_CART.BEFORE_UPDATE_ITEM_QUANTITY,
          this.beforeUpdateItemQuantity
        );

        window.BAO.eventBus.removeEventListener(
          window.BAO.EVENTS.PUSH_CART.AFTER_UPDATE_ITEM_QUANTITY,
          this.afterUpdateItemQuantity
        );

        window.BAO.eventBus.removeEventListener(
          window.BAO.EVENTS.PUSH_CART.BEFORE_ADD,
          this.onBeforeCartAdd
        );

        window.BAO.eventBus.removeEventListener(
          window.BAO.EVENTS.PUSH_CART.AFTER_ADD,
          this.onAfterCartAdd
        );
      }

      beginCartUpdate() {
        this._cartUpdatePending += 1;
        this.loading = true;
      }

      endCartUpdate() {
        this._cartUpdatePending = Math.max(0, this._cartUpdatePending - 1);

        if (this._cartUpdatePending === 0) {
          this.loading = false;
        }
      }

      onBeforeCartAdd() {
        this.drawers.activeDrawerKey = this.drawer.getAttribute("key");
        this.beginCartUpdate();
      }

      beforeUpdateItemQuantity() {
        this.beginCartUpdate();
      }

      onAfterCartAdd(event) {
        try {
          this.closeMultibuyPicker();

          if (!event.detail?.error) {
            this.updateCartSections(event.detail);
          }
        } catch (error) {
          console.error("Failed to update push cart after add", error);
        } finally {
          // Only balance adds that called beginCartUpdate via BEFORE_ADD
          if (this._cartUpdatePending > 0) {
            this.endCartUpdate();
          }
        }
      }

      closeMultibuyPicker() {
        const multibuy = this.querySelector("push-cart-multibuy");

        if (multibuy) {
          multibuy.closePicker({ immediate: true });
          return;
        }

        const picker = this.querySelector("[data-multibuy-picker]");
        if (!picker || picker.hidden) {
          return;
        }

        picker.classList.remove("psh-MultibuyPicker--visible");
        picker.hidden = true;
        picker.setAttribute("aria-hidden", "true");

        const sizesEl = picker.querySelector("[data-multibuy-picker-sizes]");
        if (sizesEl) {
          if (typeof sizesEl.replaceChildren === "function") {
            sizesEl.replaceChildren();
          } else {
            sizesEl.innerHTML = "";
          }
        }

        this.classList.remove("psh-Cart--multibuy-picker-open");

        const drawer = this.closest("site-drawer");
        drawer?.classList.remove("drw-Drawer--multibuy-picker-open");
      }

      afterUpdateItemQuantity(event) {
        try {
          if (!event.detail?.error) {
            this.updateCartSections(event.detail);
          }
        } catch (error) {
          console.error("Failed to update push cart after quantity change", error);
        } finally {
          this.endCartUpdate();
        }

        setTimeout(() => {
          this.checkBasketPreOrders();
        }, 2000);
      }

      updateCartSections(detail = {}) {
        const stateSections = detail?.state?.sections;
        const deferredSections =
          detail?.deferredSections ||
          window.BAO.utils.sectionRenderer.pushCartDeferredSectionNames;
        const sectionsUrl =
          detail?.sectionsUrl ||
          window.BAO.utils.sectionRenderer.pushCartSectionsUrl;

        if (stateSections != null && typeof stateSections === "object") {
          this.renderSections(
            stateSections,
            window.BAO.utils.sectionRenderer.pushCartPrioritySectionNames
          );
        }

        this.queueDeferredSections(deferredSections, sectionsUrl);
        this.checkBasketPreOrders();
        this.syncCartItemsStickyFooter();
      }

      renderSections(stateSections, sectionIds = null) {
        super.renderSections(stateSections, sectionIds);
        this.syncCartItemsStickyFooter();
      }

      syncCartItemsStickyFooter() {
        const items = this.querySelector(".psh-CartItems");
        if (!items) {
          return;
        }

        const stickyFooterEnabled = this.hasAttribute("data-sticky-footer");
        const hasMultibuy = Boolean(this.querySelector(".psh-Multibuy"));

        if (!stickyFooterEnabled) {
          items.classList.remove("psh-CartItems--sticky-footer");
          items.classList.remove("psh-CartItems--sticky-footer-multibuy");
          return;
        }

        items.classList.add("psh-CartItems--sticky-footer");
        items.classList.toggle(
          "psh-CartItems--sticky-footer-multibuy",
          hasMultibuy
        );
      }

      queueDeferredSections(sectionNames, sectionsUrl) {
        if (!sectionNames?.length) {
          return;
        }

        const fetchId = ++this._deferredFetchId;

        window.BAO.utils.sectionRenderer
          .fetchSections(sectionNames, sectionsUrl)
          .then((sections) => {
            if (fetchId !== this._deferredFetchId) {
              return;
            }

            if (sections != null && typeof sections === "object") {
              this.renderSections(sections, sectionNames);
            }
          })
          .catch((error) => {
            console.error("Failed to fetch deferred cart sections", error);
          });
      }

      toggleLoadingOverlay(show) {
        const overlay = this.querySelector(".loading-state");

        if (overlay) {
          overlay.style.display = show ? "block" : "none";
        }
      }

      checkBasketPreOrders() {
        const preOrders = this.querySelectorAll(".psh-CartItem--pill-msg");
        const preOrderMsg = document.querySelector(
          ".psh-Cart_PreOrder .message"
        );

        if (preOrderMsg == null) return;

        if (preOrders.length > 0) {
          preOrderMsg.classList.add("show");
        } else {
          preOrderMsg.classList.remove("show");
        }
      }

      get drawers() {
        return this.closest("site-drawers");
      }

      get drawer() {
        return this.closest("site-drawer");
      }
    }
  );
}
